import { useEffect, useState, useMemo, useRef } from 'react';
import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api, { TIMEOUT_UPLOAD } from '../services/api';
import toast from 'react-hot-toast';
import {
  Plus, CheckCircle, Clock, AlertCircle, Pause, Play, Trash2, Eye, RotateCcw,
  WifiOff, Upload, CheckSquare, Square, AlertTriangle, ShieldAlert, Timer, Smartphone
} from 'lucide-react';
import { SkeletonTableRows } from '../components/Skeletons';
import { useT } from '../i18n';

export default function Campanhas() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useT();
  const params = new URLSearchParams(location.search);
  const novaId = params.get('novaId');
  const novaIdNum = novaId ? Number(novaId) : null;

  const statusConfig = useMemo(() => ({
    finalizado:          { cls: 'badge badge-success', label: t('campanhas.status_finalizado'),       icon: <CheckCircle size={14} /> },
    pendente:            { cls: 'badge badge-warning', label: t('campanhas.status_pendente'),         icon: <Clock size={14} /> },
    processando:         { cls: 'badge badge-info',    label: t('campanhas.status_processando'),      icon: <Clock size={14} /> },
    pausada:             { cls: 'badge badge-neutral', label: t('campanhas.status_pausada'),          icon: <Pause size={14} /> },
    sem_instancia:       { cls: 'badge badge-amber',   label: t('campanhas.status_sem_instancia'),    icon: <WifiOff size={14} /> },
    aguardando_horario:  { cls: 'badge badge-amber',   label: t('campanhas.status_aguardando_horario'), icon: <Timer size={14} /> },
    limite_diario:       { cls: 'badge badge-amber',   label: t('campanhas.status_limite_diario'),    icon: <Timer size={14} /> },
    bloqueio_detectado:  { cls: 'badge badge-danger',  label: t('campanhas.status_bloqueio_detectado'), icon: <ShieldAlert size={14} /> },
    plano_vencido:       { cls: 'badge badge-danger',  label: t('campanhas.status_plano_vencido'),    icon: <ShieldAlert size={14} /> },
    erro:                { cls: 'badge badge-danger',  label: t('campanhas.status_erro'),             icon: <AlertCircle size={14} /> },
  }), [t]);

  const statusTooltip = useMemo(() => ({
    limite_diario: t('campanhas.tooltip_limite_diario'),
    bloqueio_detectado: t('campanhas.tooltip_bloqueio'),
    sem_instancia: t('campanhas.tooltip_sem_instancia'),
    aguardando_horario: t('campanhas.tooltip_aguardando_horario'),
  }), [t]);

  const [campanhas, setCampanhas] = useState([]);
  const [detalhe, setDetalhe] = useState(null);
  const [reenvio, setReenvio] = useState(null);
  const [contatos, setContatos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [contatosSelecionados, setContatosSelecionados] = useState([]);
  const [gruposSelecionados, setGruposSelecionados] = useState([]);
  const [importadosReenvio, setImportadosReenvio] = useState([]);
  const [importandoReenvio, setImportandoReenvio] = useState(false);
  const [instanciasDisponiveis, setInstanciasDisponiveis] = useState([]);
  const [instanciaReenvio, setInstanciaReenvio] = useState('');
  const [iniciarEnvio, setIniciarEnvio] = useState(null);
  const [aceitouTermos, setAceitouTermos] = useState(false);
  const [iniciando, setIniciando] = useState(false);
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [buscaReenvio, setBuscaReenvio] = useState('');
  const [gruposAberto, setGruposAberto] = useState(false);
  const [trocarInstancia, setTrocarInstancia] = useState(null); // { id, nome, instancia }
  const [novaInstancia, setNovaInstancia] = useState('');
  const [salvandoInstancia, setSalvandoInstancia] = useState(false);
  const linhaDestacadaRef = useRef(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get('/campanha/listar');
      setCampanhas(data);
    } finally {
      setPrimeiraCarga(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useAutoRefresh(carregar, 15000);

  useEffect(() => {
    api.get('/envio/contatos/listar').then(r => setContatos(r.data));
    api.get('/grupo/listar').then(r => setGrupos(r.data));
    api.get('/whatsapp/instancias').then(r => {
      const lista = Array.isArray(r.data) ? r.data : [];
      const nomes = lista
        .map(i => i?.instance?.instanceName || i?.instanceName || i?.name)
        .filter(Boolean);
      setInstanciasDisponiveis(nomes);
    }).catch(() => {});
  }, []);

  const campanhasOrdenadas = useMemo(() => {
    if (!novaIdNum) return campanhas;
    const alvo = campanhas.find(c => c.id === novaIdNum);
    if (!alvo) return campanhas;
    return [alvo, ...campanhas.filter(c => c.id !== novaIdNum)];
  }, [campanhas, novaIdNum]);

  useEffect(() => {
    if (novaIdNum && linhaDestacadaRef.current) {
      linhaDestacadaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [novaIdNum, campanhas]);

  async function handlePausar(id) {
    try {
      await api.patch(`/campanha/${id}/pausar`);
      toast.success(t('campanhas.toast_pausada'));
      carregar();
    } catch { toast.error(t('campanhas.toast_erro_pausar')); }
  }

  async function handleRetomar(id) {
    try {
      await api.patch(`/campanha/${id}/retomar`);
      toast.success(t('campanhas.toast_retomada'));
      carregar();
    } catch { toast.error(t('campanhas.toast_erro_retomar')); }
  }

  async function handleReativar(id) {
    try {
      await api.patch(`/campanha/${id}/retomar`);
      toast.success(t('campanhas.toast_reativada'));
      carregar();
    } catch { toast.error(t('campanhas.toast_erro_reativar')); }
  }

  async function handleDeletar(id, nome) {
    if (!window.confirm(t('campanhas.confirm_deletar', { nome }))) return;
    try {
      await api.delete(`/campanha/${id}`);
      toast.success(t('campanhas.toast_deletada'));
      carregar();
    } catch { toast.error(t('campanhas.toast_erro_deletar')); }
  }

  async function handleDetalhe(id) {
    try {
      const { data } = await api.get(`/campanha/${id}`);
      setDetalhe(data);
    } catch { toast.error(t('campanhas.toast_erro_detalhe')); }
  }

  function abrirTrocarInstancia(c) {
    setTrocarInstancia(c);
    setNovaInstancia(c.instancia || (instanciasDisponiveis[0] || ''));
  }

  async function handleSalvarInstancia() {
    if (!novaInstancia) { toast.error('Selecione uma instância'); return; }
    setSalvandoInstancia(true);
    try {
      await api.patch(`/campanha/${trocarInstancia.id}/instancia`, { instancia: novaInstancia });
      toast.success('Instância atualizada');
      setTrocarInstancia(null);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao atualizar instância');
    } finally {
      setSalvandoInstancia(false);
    }
  }

  async function handleReenviar() {
    try {
      await api.post(`/campanha/${reenvio.id}/reenviar`, {
        contato_ids: contatosSelecionados,
        grupo_ids: gruposSelecionados,
        instancia: instanciaReenvio || undefined
      });
      toast.success(t('campanhas.toast_reenviada'));
      fecharReenvio();
      carregar();
    } catch { toast.error(t('campanhas.toast_erro_reenviar')); }
  }

  function abrirReenvio(c) {
    setReenvio(c);
    setContatosSelecionados([]);
    setGruposSelecionados([]);
    setImportadosReenvio([]);
    setInstanciaReenvio(c.instancia || '');
  }

  function fecharReenvio() {
    setReenvio(null);
    setContatosSelecionados([]);
    setGruposSelecionados([]);
    setImportadosReenvio([]);
    setInstanciaReenvio('');
    setBuscaReenvio('');
    setGruposAberto(false);
  }

  async function handleImportCSVReenvio(e) {
    const arquivo = e.target.files[0];
    if (!arquivo) return;
    setImportandoReenvio(true);
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      const { data } = await api.post('/importar/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: TIMEOUT_UPLOAD
      });
      toast.success(t('campanhas.toast_csv_importados', { n: data.importados }));
      const r = await api.get('/envio/contatos/listar');
      setContatos(r.data);
      const novosIds = data.contatos.map(c => c.id);
      setImportadosReenvio(prev => {
        const mapa = new Map(prev.map(c => [c.id, c]));
        data.contatos.forEach(c => mapa.set(c.id, c));
        return Array.from(mapa.values());
      });
      setContatosSelecionados(prev => [
        ...prev,
        ...novosIds.filter(id => !prev.includes(id))
      ]);
    } catch {
      toast.error(t('campanhas.toast_csv_erro'));
    } finally {
      setImportandoReenvio(false);
      e.target.value = '';
    }
  }

  function toggleTodosContatosReenvio() {
    if (contatosSelecionados.length === contatos.length && contatos.length > 0) {
      setContatosSelecionados([]);
    } else {
      setContatosSelecionados(contatos.map(c => c.id));
    }
  }

  function abrirIniciarEnvio(c) {
    setIniciarEnvio(c);
    setAceitouTermos(false);
  }

  async function confirmarIniciarEnvio() {
    if (!iniciarEnvio || !aceitouTermos) return;
    setIniciando(true);
    try {
      await api.patch(`/campanha/${iniciarEnvio.id}/retomar`);
      toast.success(t('campanhas.toast_iniciado'));
      setIniciarEnvio(null);
      setAceitouTermos(false);
      if (novaIdNum === iniciarEnvio.id) {
        navigate('/campanhas', { replace: true });
      }
      carregar();
    } catch {
      toast.error(t('campanhas.toast_erro_iniciar'));
    } finally {
      setIniciando(false);
    }
  }

  function formatarHorario(inicio, fim) {
    if (!inicio && !fim) return '—';
    if (inicio && fim) return `${inicio} – ${fim}`;
    return inicio || fim;
  }

  function formatarIntervalo(min, max) {
    if (!min && !max) return t('campanhas.intervalo_imediato');
    if (min && max) return `${min}–${max}s`;
    return `${min || max}s`;
  }

  const todosContatosMarcados = contatosSelecionados.length === contatos.length && contatos.length > 0;

  return (
    <div>
      <div className="page-h">
        <div>
          <h1 className="page-h-title">{t('campanhas.titulo')}</h1>
          <p className="page-h-sub">{t('campanhas.subtitulo')}</p>
        </div>
        <div className="page-h-actions">
          <Link to="/campanhas/nova" className="btn btn-primary">
            <Plus size={16} /> {t('campanhas.btn_nova')}
          </Link>
        </div>
      </div>

      {/* Modal de detalhes */}
      {detalhe && createPortal(
        <div className="modal-backdrop" onClick={() => setDetalhe(null)}>
          <div onClick={e => e.stopPropagation()} className="modal modal-md">
            <div className="modal-h">
              <h2 className="modal-title">{detalhe.nome}</h2>
              <button onClick={() => setDetalhe(null)} className="modal-close">×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '1rem' }}>
              {[
                [t('campanhas.detalhe_status'), statusConfig[detalhe.status]?.label || detalhe.status],
                [t('campanhas.detalhe_tipo'), detalhe.tipo],
                [t('campanhas.detalhe_instancia'), detalhe.instancia || '—'],
                [t('campanhas.detalhe_intervalo'), formatarIntervalo(detalhe.intervalo_min, detalhe.intervalo_max)],
                [t('campanhas.detalhe_bloco'), detalhe.bloco_tamanho && detalhe.bloco_intervalo
                  ? t('campanhas.detalhe_bloco_valor', { tamanho: detalhe.bloco_tamanho, intervalo: detalhe.bloco_intervalo })
                  : '—'],
                [t('campanhas.detalhe_horario'), formatarHorario(detalhe.hora_inicio, detalhe.hora_fim)],
                [t('campanhas.detalhe_progresso'), t('campanhas.detalhe_progresso_valor', { n: detalhe.ultimo_contato_idx || 0 })],
              ].map(([label, valor]) => (
                <div key={label} style={{ background: 'var(--color-bg-soft)', borderRadius: 'var(--radius)', padding: '10px 12px', border: '1px solid var(--color-border-soft)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginBottom: '2px' }}>{label}</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{valor}</div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <p className="label">{t('campanhas.detalhe_mensagem')}</p>
              <div className="info-box" style={{ fontSize: '13px' }}>
                {detalhe.mensagem}
              </div>
            </div>

            {detalhe.contatos && detalhe.contatos.length > 0 && (
              <div>
                <p className="label">{t('campanhas.detalhe_contatos', { n: detalhe.contatos.length })}</p>
                <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                  {detalhe.contatos.map(c => (
                    <div key={c.id} style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-divider)', fontSize: '13px' }}>
                      <span style={{ fontWeight: 500 }}>{c.nome || '—'}</span>
                      <span className="muted" style={{ marginLeft: '8px' }}>{c.telefone}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      , document.body)}

      {/* Modal de reenvio */}
      {reenvio && createPortal(
        <div className="modal-backdrop" onClick={fecharReenvio}>
          <div onClick={e => e.stopPropagation()} className="modal modal-md">
            <div className="modal-h">
              <h2 className="modal-title">{t('campanhas.reenvio_titulo', { nome: reenvio.nome })}</h2>
              <button onClick={fecharReenvio} className="modal-close">×</button>
            </div>

            <div className="info-box" style={{ marginBottom: '1rem' }}>
              {t('campanhas.reenvio_hint')}
            </div>

            <div className="field">
              <label className="label">{t('campanhas.reenvio_instancia_label')}</label>
              {instanciasDisponiveis.length > 0 ? (
                <select value={instanciaReenvio} onChange={e => setInstanciaReenvio(e.target.value)} className="select">
                  {instanciasDisponiveis.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              ) : (
                <input value={instanciaReenvio} onChange={e => setInstanciaReenvio(e.target.value)}
                  placeholder={reenvio.instancia || 'disparador01'}
                  className="input"
                />
              )}
            </div>

            {grupos.length > 0 && (
              <div className="field" style={{ marginBottom: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setGruposAberto(v => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', background: 'var(--color-bg-soft)',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
                    padding: '8px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                    color: 'var(--color-text)'
                  }}
                >
                  <span>{t('campanhas.reenvio_grupos_label')} {gruposSelecionados.length > 0 && <span style={{ color: 'var(--color-accent)', fontWeight: 700 }}>{t('campanhas.reenvio_grupos_selecionados', { n: gruposSelecionados.length })}</span>}</span>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', transition: 'transform 200ms', display: 'inline-block', transform: gruposAberto ? 'rotate(180deg)' : 'none' }}>▼</span>
                </button>
                {gruposAberto && (
                  <div className="row" style={{ marginTop: '8px', flexWrap: 'wrap' }}>
                    {grupos.map(g => (
                      <div key={g.id}
                        onClick={() => setGruposSelecionados(prev =>
                          prev.includes(g.id) ? prev.filter(x => x !== g.id) : [...prev, g.id]
                        )}
                        className={`pill${gruposSelecionados.includes(g.id) ? ' is-active' : ''}`}
                      >
                        {g.nome} · {g.total_contatos}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="row" style={{ marginBottom: '10px' }}>
              <button onClick={toggleTodosContatosReenvio} className="btn btn-secondary btn-sm">
                {todosContatosMarcados ? <CheckSquare size={14} /> : <Square size={14} />}
                {todosContatosMarcados ? t('campanhas.reenvio_desmarcar_todos') : t('campanhas.reenvio_selecionar_todos')}
              </button>
              <label className="btn btn-sm" style={{ background: '#f0fdf4', color: 'var(--color-success)', borderColor: '#86efac', cursor: 'pointer' }}>
                <Upload size={14} />
                {importandoReenvio ? t('campanhas.reenvio_importando') : t('campanhas.reenvio_importar_csv')}
                <input type="file" accept=".csv" onChange={handleImportCSVReenvio} style={{ display: 'none' }} />
              </label>
              <span className="muted" style={{ fontSize: '12px', marginLeft: 'auto' }}>
                {t('campanhas.reenvio_contadores', { sel: contatosSelecionados.length, total: contatos.length })}
              </span>
            </div>

            {importadosReenvio.length > 0 && (() => {
              const ids = importadosReenvio.map(c => c.id);
              const marcados = ids.filter(id => contatosSelecionados.includes(id)).length;
              const todosMarcados = marcados === ids.length;
              return (
                <div className="info-box info-box-success" style={{ padding: '10px 12px', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <p style={{ fontSize: '12px', fontWeight: 600, margin: 0 }}>
                      {t('campanhas.reenvio_importados_titulo', { n: importadosReenvio.length, sel: marcados })}
                    </p>
                    <button onClick={() => {
                      if (todosMarcados) {
                        setContatosSelecionados(prev => prev.filter(id => !ids.includes(id)));
                      } else {
                        setContatosSelecionados(prev => {
                          const set = new Set(prev); ids.forEach(id => set.add(id));
                          return Array.from(set);
                        });
                      }
                    }} className="btn btn-sm" style={{ background: '#fff', borderColor: '#86efac', color: 'var(--color-success)' }}>
                      {todosMarcados ? t('campanhas.reenvio_desmarcar_todos') : t('campanhas.reenvio_selecionar_todos')}
                    </button>
                  </div>
                  <div style={{ maxHeight: '160px', overflowY: 'auto', background: '#fff', borderRadius: 'var(--radius-sm)', border: '1px solid #d1fae5' }}>
                    {importadosReenvio.map(c => (
                      <label key={c.id} style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '6px 10px', cursor: 'pointer',
                        borderBottom: '1px solid var(--color-divider)',
                        background: contatosSelecionados.includes(c.id) ? '#f0fdf4' : '#fff'
                      }}>
                        <input type="checkbox"
                          checked={contatosSelecionados.includes(c.id)}
                          onChange={() => setContatosSelecionados(prev =>
                            prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id]
                          )}
                          style={{ accentColor: 'var(--color-success)' }}
                        />
                        <span style={{ fontSize: '12px' }}>{c.nome || '—'} · {c.telefone}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })()}

            <p className="label">{t('campanhas.reenvio_selecionar_contatos')}</p>
            <input
              className="input"
              placeholder={t('campanhas.reenvio_busca_placeholder')}
              value={buscaReenvio}
              onChange={e => setBuscaReenvio(e.target.value)}
              style={{ marginBottom: '6px' }}
            />
            <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '1rem' }}>
              {contatos
                .filter(c => {
                  const q = buscaReenvio.toLowerCase();
                  return !q || (c.nome || '').toLowerCase().includes(q) || (c.telefone || '').includes(q);
                })
                .map(c => (
                  <div key={c.id}
                    onClick={() => setContatosSelecionados(prev =>
                      prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id]
                    )}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 12px', cursor: 'pointer',
                      borderBottom: '1px solid var(--color-divider)',
                      background: contatosSelecionados.includes(c.id) ? 'var(--color-accent-soft)' : 'var(--color-surface)'
                    }}>
                    <input type="checkbox" readOnly checked={contatosSelecionados.includes(c.id)} style={{ accentColor: 'var(--color-accent)' }} />
                    <span style={{ fontSize: '13px' }}>{c.nome || '—'} · {c.telefone}</span>
                  </div>
                ))
              }
              {contatos.filter(c => {
                const q = buscaReenvio.toLowerCase();
                return !q || (c.nome || '').toLowerCase().includes(q) || (c.telefone || '').includes(q);
              }).length === 0 && (
                <div className="empty" style={{ padding: '1rem' }}>{t('campanhas.reenvio_vazio')}</div>
              )}
            </div>

            <div className="row">
              <button onClick={fecharReenvio} className="btn btn-secondary">{t('campanhas.reenvio_cancelar')}</button>
              <button onClick={handleReenviar} className="btn btn-primary" style={{ flex: 1 }}>
                {t('campanhas.reenvio_btn')}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal de confirmação de início de envio */}
      {iniciarEnvio && createPortal(
        <div className="modal-backdrop" style={{ zIndex: 1001 }} onClick={() => !iniciando && setIniciarEnvio(null)}>
          <div onClick={e => e.stopPropagation()} className="modal modal-sm">
            <div className="row" style={{ marginBottom: '1rem' }}>
              <AlertTriangle size={24} color="var(--color-warning)" />
              <h2 className="modal-title" style={{ fontSize: '17px' }}>{t('campanhas.confirmar_inicio_titulo')}</h2>
            </div>

            <p style={{ fontSize: '14px', color: 'var(--color-text-soft)', marginBottom: '10px' }}>
              {t('campanhas.confirmar_inicio_descricao')} <strong style={{ color: 'var(--color-text)' }}>{iniciarEnvio.nome}</strong>.
            </p>

            <div className="info-box info-box-warn" style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 6px' }}>{t('campanhas.confirmar_aviso_titulo')}</p>
              <p style={{ fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
                {t('campanhas.confirmar_aviso_texto')}
              </p>
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', marginBottom: '1.25rem' }}>
              <input type="checkbox" checked={aceitouTermos}
                onChange={e => setAceitouTermos(e.target.checked)}
                style={{ marginTop: '3px', accentColor: 'var(--color-primary)', width: '16px', height: '16px' }}
              />
              <span style={{ fontSize: '13px', color: 'var(--color-text)', lineHeight: 1.5 }}>
                {t('campanhas.confirmar_aceitar_termos')}
              </span>
            </label>

            <div className="row">
              <button onClick={() => setIniciarEnvio(null)} disabled={iniciando} className="btn btn-secondary">
                {t('campanhas.confirmar_cancelar')}
              </button>
              <button onClick={confirmarIniciarEnvio}
                disabled={!aceitouTermos || iniciando}
                className="btn"
                style={{
                  flex: 1,
                  background: aceitouTermos ? 'var(--color-success)' : '#9ca3af',
                  color: '#fff',
                  borderColor: aceitouTermos ? 'var(--color-success)' : '#9ca3af'
                }}>
                {iniciando ? t('campanhas.confirmar_iniciando') : t('campanhas.confirmar_btn_iniciar')}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {trocarInstancia && createPortal(
        <div className="modal-backdrop" style={{ zIndex: 1001 }} onClick={() => !salvandoInstancia && setTrocarInstancia(null)}>
          <div onClick={e => e.stopPropagation()} className="modal modal-sm">
            <div className="row" style={{ marginBottom: '1rem' }}>
              <Smartphone size={22} color="var(--color-accent)" />
              <h2 className="modal-title" style={{ fontSize: '17px' }}>Trocar instância</h2>
            </div>

            <p style={{ fontSize: '13px', color: 'var(--color-text-soft)', marginBottom: '10px' }}>
              Campanha: <strong style={{ color: 'var(--color-text)' }}>{trocarInstancia.nome}</strong><br/>
              Instância atual: <code style={{ fontSize: 12 }}>{trocarInstancia.instancia || '— sem instância —'}</code>
            </p>

            <label className="label" style={{ marginTop: 12 }}>Nova instância</label>
            {instanciasDisponiveis.length > 0 ? (
              <select value={novaInstancia} onChange={e => setNovaInstancia(e.target.value)} className="select">
                {instanciasDisponiveis.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            ) : (
              <div className="info-box info-box-warn" style={{ fontSize: 12 }}>
                Nenhuma instância conectada. Vá em <Link to="/whatsapp">WhatsApp</Link> e conecte uma instância primeiro.
              </div>
            )}

            <div className="row" style={{ gap: '8px', marginTop: '1.25rem' }}>
              <button onClick={() => setTrocarInstancia(null)} disabled={salvandoInstancia} className="btn btn-secondary" style={{ flex: 1 }}>
                Cancelar
              </button>
              <button
                onClick={handleSalvarInstancia}
                disabled={salvandoInstancia || !novaInstancia || instanciasDisponiveis.length === 0}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                {salvandoInstancia ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Tabela */}
      <div className="card card-flush">
        <table className="table">
          <thead>
            <tr>
              {[
                t('campanhas.col_nome'),
                t('campanhas.col_status'),
                t('campanhas.col_horario'),
                t('campanhas.col_intervalo'),
                t('campanhas.col_criada_em'),
                t('campanhas.col_acoes'),
              ].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {primeiraCarga && campanhas.length === 0 && <SkeletonTableRows rows={4} cols={6} />}
            {!primeiraCarga && campanhasOrdenadas.map(c => {
              const sc = statusConfig[c.status] || { cls: 'badge badge-neutral', label: c.status, icon: null };
              const destacar = c.id === novaIdNum;
              return (
                <tr key={c.id}
                  ref={destacar ? linhaDestacadaRef : null}
                  style={{
                    background: destacar ? '#fef9c3' : undefined,
                    boxShadow: destacar ? 'inset 3px 0 0 #facc15' : undefined
                  }}>
                  <td style={{ fontWeight: 500 }}>
                    {c.nome}
                    {destacar && (
                      <span className="badge" style={{
                        marginLeft: '8px', background: '#facc15', color: '#713f12', fontWeight: 600
                      }}>{t('campanhas.badge_nova')}</span>
                    )}
                  </td>
                  <td>
                    <span className={sc.cls} title={statusTooltip[c.status] || ''}>
                      {sc.icon} {sc.label}
                    </span>
                    {statusTooltip[c.status] && (
                      <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '3px', maxWidth: '160px', lineHeight: 1.4 }}>
                        {statusTooltip[c.status]}
                      </div>
                    )}
                  </td>
                  <td className="table-cell-muted">
                    {formatarHorario(c.hora_inicio, c.hora_fim)}
                  </td>
                  <td className="table-cell-muted">
                    {formatarIntervalo(c.intervalo_min, c.intervalo_max)}
                  </td>
                  <td className="table-cell-muted">
                    {new Date(c.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td>
                    <div className="row-tight" style={{ flexWrap: 'wrap', gap: '6px' }}>
                      {c.status === 'pausada' && (
                        <button onClick={() => abrirIniciarEnvio(c)} title={t('campanhas.btn_iniciar_envio')}
                          className="btn btn-sm"
                          style={{ background: 'var(--color-success)', color: '#fff', borderColor: 'var(--color-success)', fontWeight: 600 }}>
                          <Play size={13} /> {t('campanhas.btn_iniciar_envio')}
                        </button>
                      )}

                      <button onClick={() => handleDetalhe(c.id)} title={t('campanhas.detalhe_titulo', { nome: c.nome })}
                        className="btn btn-icon btn-sm btn-secondary">
                        <Eye size={13} />
                      </button>

                      {(c.status === 'pendente' || c.status === 'processando') && (
                        <button onClick={() => handlePausar(c.id)} title={t('campanhas.btn_pausar')}
                          className="btn btn-icon btn-sm btn-warning">
                          <Pause size={13} />
                        </button>
                      )}

                      {c.status === 'sem_instancia' && (
                        <button onClick={() => handleRetomar(c.id)} title={t('campanhas.btn_retomar')}
                          className="btn btn-icon btn-sm"
                          style={{ background: '#f0fdf4', color: 'var(--color-success)', borderColor: '#86efac' }}>
                          <Play size={13} />
                        </button>
                      )}

                      {c.status === 'bloqueio_detectado' && (
                        <button onClick={() => handleReativar(c.id)}
                          title={t('campanhas.tooltip_bloqueio')}
                          className="btn btn-sm"
                          style={{ background: '#fef3c7', color: '#92400e', borderColor: '#fcd34d', fontSize: '12px' }}>
                          <ShieldAlert size={13} /> {t('campanhas.btn_reativar')}
                        </button>
                      )}

                      {c.status === 'finalizado' && (
                        <button onClick={() => abrirReenvio(c)} title={t('campanhas.btn_reenviar')}
                          className="btn btn-icon btn-sm btn-info">
                          <RotateCcw size={13} />
                        </button>
                      )}

                      <button onClick={() => abrirTrocarInstancia(c)} title="Trocar instância"
                        className="btn btn-icon btn-sm btn-secondary">
                        <Smartphone size={13} />
                      </button>

                      <button onClick={() => handleDeletar(c.id, c.nome)} title={t('campanhas.btn_deletar')}
                        className="btn btn-icon btn-sm btn-danger">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!primeiraCarga && campanhas.length === 0 && (
              <tr><td colSpan={6}>
                <div className="empty">{t('campanhas.vazio')}</div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
